import React, { useState, useEffect } from "react";
import L from "leaflet";
import Line from "../../../assets/line2.svg";
import { FaMapMarkerAlt, FaPhoneAlt } from "react-icons/fa";
import { MapContainer, TileLayer, Marker, useMap, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import CustomMarkerImg from "../../../assets/mapmarker.png";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { ContactUs } from "../../../redux/Slices/AuthSlices/AuthSlice";
import { toast } from "react-toastify";
import Loader from "../../../assets/loader4.gif";

export default function Header() {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      name: "",
      company_name: "",
      phone_number: "",
      email: "",
      message: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      company_name: Yup.string().required("Company name is required"),
      phone_number: Yup.string()
        .matches(/^\+?[0-9]{10,15}$/, "Invalid phone number format")
        .required("Phone number is required"),
      email: Yup.string()
        .matches(
          /^[a-zA-Z0-9._%+-]+@(gmail\.com|yahoo\.com|outlook\.com|hotmail\.com|icloud\.com|aol\.com|zoho\.com|protonmail\.com|yandex\.com)$/,
          "Only valid emails are allowed"
        )
        .required("Email is required"),
      message: Yup.string().required("Message is required"),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setLoading(true);
      try {
        await dispatch(ContactUs(values)).unwrap();
        resetForm();
        setLoading(false);
      } catch (error) {
        toast.error(`Failed to send message: ${error}`);
        setLoading(false);
      } finally {
        setSubmitting(false);
        setLoading(false);
      }
    },
  });  

  const customIcon = L.icon({
    iconUrl: CustomMarkerImg,
    iconSize: [58, 60],
    iconAnchor: [19, 38],
    popupAnchor: [0, -38],
  });

  const locationDetails = {
    address: "2117 Lake Ave, Altadena, CA 91001, USA",
    city: "Altadena",
  };

  const toggleFullScreen = () => setIsFullScreen(!isFullScreen);

  const AddZoomControl = () => {
    const map = useMap();

    useEffect(() => {
      if (!map) return;

      const zoomControl = L.control
        .zoom({
          position: "bottomright",
        })
        .addTo(map);

      return () => {
        map.removeControl(zoomControl);
      };
    }, [map]);

    return null;
  };

  return (
    <div className="bg-white py-8 px-5 flex flex-col items-center mb-4">
      <div className="text-center w-full md:w-[90%] mb-8">
        <h2 className="text-3xl sm:text-4xl lg:text-[3rem] lg:leading-[3.5rem] font-bold">
          We Want to{" "}
          <span className="relative inline-block">
            Hear{" "}
            <img
              src={Line}
              alt="Underline decoration"
              className="absolute bottom-0 left-0 w-full top-9 lg:top-[58px]"
            />
          </span>{' '}
          from You
        </h2>
        <p className="text-xl md:text-[28px] my-4 mt-3">
          Fill out the form below, and leave us a message{" "}
        </p>
      </div>

      <div className="flex flex-col lg:flex-row items-center lg:items-start p-4 lg:p-12 lg:px-40 gap-8 lg:gap-24 w-full max-w-screen-2xl mx-auto">
        <div className="w-full lg:w-[50%] order-1 overflow-hidden relative">
          <MapContainer
            center={[34.18223, -118.13191]}
            zoom={10}
            className="h-[300px] w-full relative z-0"
            style={{ zIndex: 0 }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            <Marker position={[34.18223, -118.13191]} icon={customIcon} />
            <AddZoomControl />
          </MapContainer>

          <div className="absolute top-2 left-2 bg-white py-2 px-3 rounded-md shadow-lg w-48 text-left z-50 border border-gray-200">
            <h3 className="text-red-500 font-bold m-0 text-sm">
              {locationDetails.city}
            </h3>
            <p className="mt-1 text-xs">{locationDetails.address}</p>
            <button
              onClick={toggleFullScreen}
              className="mt-1 hover:underline text-xs text-blue-500"
            >
              View larger map
            </button>
          </div>

          <div className="py-4 pt-6 bg-white">
            <h3 className="text-gray-700 text-xs font-semibold my-3">
              REACH US THROUGH
            </h3>
            <div className="flex items-center text-gray-700 mb-3">
              <FaMapMarkerAlt className="mr-2 text-blue-500" />
              <span>2117 Lake Ave, Altadena, CA 91001, USA</span>
            </div>
            <div className="flex items-center text-gray-700">
              <FaPhoneAlt className="mr-2 text-blue-500" />
              <span>0236 8345 092</span>
            </div>
          </div>
        </div>

        <div className="w-full lg:w-[50%] order-2 rounded-lg px-0 py-6 lg:p-0">
          <form onSubmit={formik.handleSubmit} className="space-y-4">
            <div>
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                className="w-full py-3 px-5 border rounded-3xl bg-[#EDEDED] focus:outline-none"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.name}
              />
              {formik.touched.name && formik.errors.name ? (
                <p className="text-red-500 text-xs mt-1">
                  {formik.errors.name}
                </p>
              ) : null}
            </div>
            <div>
              <input
                type="text"
                name="company_name"
                placeholder="Company Name"
                className="w-full py-3 px-5 border rounded-3xl bg-[#EDEDED] focus:outline-none"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.company_name}
              />
              {formik.touched.company_name && formik.errors.company_name ? (
                <p className="text-red-500 text-xs mt-1">
                  {formik.errors.company_name}
                </p>
              ) : null}
            </div>
            <div>
              <input
                type="tel"
                name="phone_number"
                placeholder="Phone Number"
                className="w-full py-3 px-5 border rounded-3xl bg-[#EDEDED] focus:outline-none"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.phone_number}
              />
              {formik.touched.phone_number && formik.errors.phone_number ? (
                <p className="text-red-500 text-xs mt-1">
                  {formik.errors.phone_number}
                </p>
              ) : null}
            </div>
            <div>
              <input
                type="email"
                name="email"
                placeholder="someone@example.com"
                className="w-full py-3 px-5 border rounded-3xl bg-[#EDEDED] focus:outline-none"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.email}
              />
              {formik.touched.email && formik.errors.email ? (
                <p className="text-red-500 text-xs mt-1">
                  {formik.errors.email}
                </p>
              ) : null}
            </div>
            <div>
              <textarea
                placeholder="Message"
                name="message"
                className="w-full py-3 px-5 border rounded-3xl bg-[#EDEDED] focus:outline-none"
                rows="3"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.message}
              ></textarea>
              {formik.touched.message && formik.errors.message ? (
                <p className="text-red-500 text-xs mt-1">
                  {formik.errors.message}
                </p>
              ) : null}
            </div>
            <button
              disabled={formik.isSubmitting}
              type="submit"
              className="w-full p-3 h-12 bg-background hover:bg-hover text-white rounded-3xl flex items-center justify-center"
            >
              {loading ? (
                <img
                  src={Loader}
                  alt="Loading..."
                  className="w-12 h-11 inline-block"
                />
              ) : (
                "Send Message"
              )}
            </button>
          </form>
        </div>
      </div>

      {isFullScreen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex justify-center items-center">
          <div className="relative w-full h-full">
            <MapContainer
              center={[34.18223, -118.13191]}
              zoom={12}
              className="w-full h-full"
              style={{ zIndex: 1 }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              <Marker position={[34.18223, -118.13191]} icon={customIcon}>
                <Popup>We are here!</Popup>
              </Marker>
              <AddZoomControl />
            </MapContainer>

            <button
              onClick={toggleFullScreen}
              className="absolute top-4 right-4 bg-white py-2 px-4 rounded-full text-gray-800 font-semibold shadow-md hover:bg-gray-100"
              style={{ zIndex: 1000 }}
            >
              Close
            </button>

            <div className="absolute bottom-4 left-4 bg-white p-2 rounded-md shadow-lg z-20">
              <h3 className="text-red-600 font-bold">{locationDetails.city}</h3>
              <p className="text-sm">{locationDetails.address}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
